import { createAsyncAction, createStandardAction } from 'typesafe-actions';

import generateLogger from '@travel/logger';

import { PLAN_DETAIL_SORT_VALUES } from 'constants/sort';
import { getIPAddress, isInSortCategory } from 'store/utils';
import { fetchPlanDetails as fetchPlanDetailsAPI } from './apis';

import { getUserIdentifier } from 'store/common/selectors';
import { getSearchEngine } from 'store/searchEngine/selectors';

import type { AppThunk } from 'typesafe-actions';
import type { PlanDetails, PlanDetailsErrors, RequestBody } from 'PlanDetails-Types';
const logger = generateLogger();

export const fetchPlanDetailsAsync = createAsyncAction(
  'FETCH_PLAN_DETAILS_REQUEST',
  'FETCH_PLAN_DETAILS_SUCCESS',
  'FETCH_PLAN_DETAILS_FAILURE',
)<undefined, PlanDetails, PlanDetailsErrors>();

export const fetchMorePlanItemsAsync = createAsyncAction(
  'FETCH_MORE_PLAN_ITEMS_REQUEST',
  'FETCH_MORE_PLAN_ITEMS_SUCCESS',
  'FETCH_MORE_PLAN_ITEMS_FAILURE',
)<undefined, PlanDetails, PlanDetailsErrors>();

export const fetchPlanDetailFilterAsync = createAsyncAction(
  'FETCH_PLAN_DETAIL_FILTER_REQUEST',
  'FETCH_PLAN_DETAIL_FILTER_SUCCESS',
  'FETCH_PLAN_DETAIL_FILTER_FAILURE',
)<undefined, PlanDetails, undefined>();

export const updatePendingFilters = createStandardAction(
  'UPDATE_PLAN_DETAILS_PENDING_FILTERS',
)<PlanDetails>();

export const fetchPlanDetails =
  (query: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient, req }) => {
    dispatch(fetchPlanDetailsAsync.request());
    const userIdentifier = getUserIdentifier(getState());
    const searchEngine = getSearchEngine(getState());

    try {
      const clonedQuery = { ...query };
      if (!isInSortCategory(PLAN_DETAIL_SORT_VALUES, clonedQuery.sort)) {
        clonedQuery.sort = PLAN_DETAIL_SORT_VALUES.Price_Low_To_High;
      }
      const response = await fetchPlanDetailsAPI(
        apiClient,
        {
          ...clonedQuery,
          rzCookie: userIdentifier,
          searchEngine,
        },
        getIPAddress(req),
      );

      dispatch(fetchPlanDetailsAsync.success(response));
    } catch (error) {
      dispatch(fetchPlanDetailsAsync.failure(error as PlanDetailsErrors));
      logger.error(error);
      throw error;
    }
  };

export const fetchMorePlanItems =
  (params: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    dispatch(fetchMorePlanItemsAsync.request());
    const searchEngine = getSearchEngine(getState());

    const state = getState();
    const { offset, limit } = state.planDetails.planDetails;
    const userIdentifier = getUserIdentifier(state);

    const clonedParams = { ...params };
    if (!isInSortCategory(PLAN_DETAIL_SORT_VALUES, clonedParams.sort)) {
      clonedParams.sort = PLAN_DETAIL_SORT_VALUES.Price_Low_To_High;
    }

    const reqBody = {
      ...clonedParams,
      offset: limit + offset,
      limit: limit,
      rzCookie: userIdentifier,
      searchEngine,
    };
    try {
      const response = await fetchPlanDetailsAPI(apiClient, reqBody);
      dispatch(fetchMorePlanItemsAsync.success(response));
    } catch (error) {
      dispatch(fetchMorePlanItemsAsync.failure(error as PlanDetailsErrors));
    }
  };

export const fetchPlanDetailsFilter =
  (query: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    dispatch(fetchPlanDetailFilterAsync.request());
    const searchEngine = getSearchEngine(getState());
    try {
      const clonedQuery = { ...query };
      if (!isInSortCategory(PLAN_DETAIL_SORT_VALUES, clonedQuery.sort)) {
        clonedQuery.sort = PLAN_DETAIL_SORT_VALUES.Price_Low_To_High;
      }

      // limit could be 1 since we will not use providers data here
      const response = await fetchPlanDetailsAPI(apiClient, {
        ...clonedQuery,
        offset: 0,
        limit: 1,
        searchEngine,
      });

      dispatch(fetchPlanDetailFilterAsync.success(response));
    } catch (error) {
      const prevState = getState().planDetails.planDetails;
      if (prevState) {
        dispatch(fetchPlanDetailFilterAsync.success(prevState));
      } else {
        dispatch(fetchPlanDetailFilterAsync.failure());
      }
    }
  };
